/**
 * Popover - Portal-based dropdown component
 * Renders dropdowns outside parent containers to prevent clipping
 */
import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

export default function Popover({
  trigger,
  children,
  isOpen,
  onClose,
  placement = 'bottom-start', // 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end'
  offset = 8,
}) {
  // Phase 6: Initialize with a safe default position instead of (0,0) to prevent off-screen rendering
  const [position, setPosition] = useState({ top: 100, left: 100 }); // Safe default instead of (0,0)
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);

  useEffect(() => {
    if (!isOpen || !triggerRef.current) return;

    const updatePosition = () => {
      // Phase 6: Wait for popover to have dimensions before positioning
      if (!popoverRef.current) {
        // Schedule another attempt after the element is rendered
        requestAnimationFrame(updatePosition);
        return;
      }

      const triggerRect = triggerRef.current.getBoundingClientRect();
      const popoverRect = popoverRef.current.getBoundingClientRect();
      
      // Phase 6: If popover has no dimensions yet, set a safe fallback position and retry
      if (popoverRect.width === 0 && popoverRect.height === 0) {
        // Phase 6: Set safe fallback position to prevent off-screen (0,0) rendering
        const [vertical, horizontal] = placement.split('-');
        let fallbackTop = triggerRect.bottom + offset;
        let fallbackLeft = horizontal === 'end' ? triggerRect.right - 360 : triggerRect.left;
        // Clamp to viewport
        fallbackTop = Math.max(16, Math.min(fallbackTop, window.innerHeight - 200));
        fallbackLeft = Math.max(16, Math.min(fallbackLeft, window.innerWidth - 360));
        setPosition({ top: fallbackTop, left: fallbackLeft });
        // Retry once element has dimensions
        requestAnimationFrame(updatePosition);
        return;
      }

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let top = 0;
      let left = 0;

      // Determine placement
      const [vertical, horizontal] = placement.split('-');
      
      // Vertical positioning
      if (vertical === 'bottom') {
        top = triggerRect.bottom + offset;
        // Check if fits below, otherwise flip to top
        if (top + popoverRect.height > viewportHeight && triggerRect.top > popoverRect.height) {
          top = triggerRect.top - popoverRect.height - offset;
        }
      } else {
        top = triggerRect.top - popoverRect.height - offset;
        // Check if fits above, otherwise flip to bottom
        if (top < 0 && triggerRect.bottom + popoverRect.height < viewportHeight) {
          top = triggerRect.bottom + offset;
        }
      }

      // Horizontal positioning
      if (horizontal === 'start') {
        left = triggerRect.left;
        // Check if fits, otherwise align right
        if (left + popoverRect.width > viewportWidth) {
          left = viewportWidth - popoverRect.width - 16; // 16px margin
        }
      } else {
        left = triggerRect.right - popoverRect.width;
        // Check if fits, otherwise align left
        if (left < 0) {
          left = triggerRect.left;
        }
      }

      // Ensure minimum margin from viewport edges
      left = Math.max(16, Math.min(left, viewportWidth - popoverRect.width - 16));
      top = Math.max(16, Math.min(top, viewportHeight - popoverRect.height - 16));

      setPosition({ top, left });
    };

    // Phase 5: Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      updatePosition();
    });
    
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen, placement, offset]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target)
      ) {
        onClose();
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  // Handle trigger rendering
  let triggerElement;
  if (typeof trigger === 'function') {
    triggerElement = trigger({ 
      ref: triggerRef, 
      'aria-expanded': isOpen, 
      'aria-haspopup': 'true' 
    });
  } else {
    // If trigger is a React element, clone it with ref
    triggerElement = trigger;
  }

  return (
    <>
      {triggerElement}
      {isOpen &&
        createPortal(
          <div
            ref={popoverRef}
            style={{
              position: 'fixed',
              top: `${position.top}px`,
              left: `${position.left}px`,
              /* Phase 2: Ensure dropdown appears above header (sticky z-index is 200, modals are 400+) */
              zIndex: 9999, /* High enough to be above header, below modals */
              pointerEvents: 'auto',
            }}
          >
            {children}
          </div>,
          document.body
        )}
    </>
  );
}
